/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SharedModule
 */

import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    NO_ERRORS_SCHEMA,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    Renderer2,
    Self,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import { ControlValueAccessor, NgControl } from '@angular/forms';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';

import {
    Observable,
    Subject,
    Subscription,
} from 'rxjs';

import { LetModule } from '@ngrx/component';
import { AviTooltipModule } from 'nsx-alb-ui-components';
import { isUndefined } from 'underscore';
import classnames from 'classnames';
import { createDropdownOption } from 'ng/shared/utils/dropdown.utils';
import { L10nService, VIPModule } from '@vmw/ngx-vip';
import { FullModalComponentService } from 'ng/shared/components/full-modal/full-modal-component.service';
import { normalizeValue } from 'ng/shared/utils';
import {
    DropdownModelSingleValue,
    DropdownModelValue,
    IAviDropdownOption,
} from './avi-dropdown.types';
import {
    containsDropdownOptionByValue,
    getOptionsHeight,
    getValue,
} from './avi-dropdown.utils';
import { AviDropdownMenuComponent } from './avi-dropdown-menu';
import { AviDropdownOptionsComponent } from './avi-dropdown-options';
import { AviDropdownSelectedValuesContainerComponent } from './avi-dropdown-selected-values-container';
import { AviDropdownSingleSelectedValueComponent } from './avi-dropdown-single-selected-value';
import { MAX_OPTIONS_LIST_HEIGHT, OPTION_HEIGHT } from './avi-dropdown.constants';
import { IsFilteredBySearchPipe } from './is-filtered-by-search.pipe';
import { IsHiddenPipe } from './is-hidden.pipe';
import './avi-dropdown.component.less';
import * as l10n from './avi-dropdown.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

export const optionsPositionsPriority: ConnectedPosition[] = [
    {
        offsetY: 3,
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
    },
    {
        offsetY: -3,
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
    },
];

/**
 * @description Dropdown component.
 * @author alextsg, Zhiqian Liu
 */
@Component({
    selector: 'avi-dropdown',
    templateUrl: './avi-dropdown.component.html',
    standalone: true,
    imports: [
        CommonModule,
        AviTooltipModule,
        LetModule,
        AviDropdownMenuComponent,
        AviDropdownOptionsComponent,
        AviDropdownSelectedValuesContainerComponent,
        AviDropdownSingleSelectedValueComponent,
        IsFilteredBySearchPipe,
        IsHiddenPipe,
        VIPModule,
    ],
    schemas: [NO_ERRORS_SCHEMA],
})
export class AviDropdownComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
    /**
     * Placeholder shown when no option(s) have been selected.
     */
    @Input()
    public placeholder = '';

    /**
     * Options to be shown for selection.
     */
    @Input()
    public options: IAviDropdownOption[] = [];

    /**
     * If true, allows user to select more than one option.
     */
    @Input()
    public multiple = false;

    /**
     * If true, hides the search input.
     */
    @Input()
    public hideSearch = false;

    /**
     * Maximum number of selectable options, only applicable for multiple selection dropdown.
     */
    @Input()
    public maxSelectableOptions = Infinity;

    /**
     * required - True if the dropdown is a required input.
     */
    @Input('required')
    private set setRequired(required: boolean | '') {
        this.required = required === '' || required;
    }

    /**
     * True to show a spinner in the dropdown options.
     */
    @Input()
    public busy ? = false;

    /**
     * List of values that should be hidden from selection.
     */
    @Input()
    public hiddenValues?: DropdownModelSingleValue[] = [];

    /**
     * Text to display underneath the field. Typically used for letting the user know that a certain
     * field must be populated first before configuring this field.
     */
    @Input()
    public helperText?: string;

    /**
     * Holds value of prepend for dropdown.
     */
    @Input()
    public prepend?: string;

    /**
     * Flag to show a value as readonly. Differs from disabled in that the input field will be
     * displayed similarly to a label.
     */
    @Input()
    public readonly ? = false;

    /**
     * Setter for the hybrid mode disabled attribute.
     * Reason to have:
     * 'disabled' is a built-in attribute bound to NgControl and this.setDisabledState is a callback
     * called by ControlValueAccessor when the value of 'disabled' changes.
     * This callback doesn't work for downgraded ajs directive since Angular NgControl is not
     * present for ajs which uses its own ngModel that no built-in 'disabled' attribute is involved.
     * Therefore, this.setDisabledState is called manually for hybrid mode whenever the value of
     * 'disable' attr is changed.
     */
    @Input('disabled')
    private set setAjsDisabled(disabled: boolean | string) {
        const isDisabled = disabled === '' || disabled;

        if (!this.hasNgControl) {
            this.setDisabledState(Boolean(isDisabled));
        }
    }

    /**
     * Optional signpost templateRef.
     */
    @Input()
    public optionSignpostTemplateRef: TemplateRef<HTMLElement>;

    /**
     * ID of the dropdown component. For accessibility grouping purposes, the focusable child div
     * has an aria-labelledby attribute set to this.
     */
    @Input()
    public id: string;

    @Input()
    public name: string;

    /**
     * Called when a user types into the search input field. Used if custom behavior should be used
     * for searching through a list of options (HTTP request for example) rather than a simple
     * filtering of the options.
     */
    @Output()
    public onSearch = new EventEmitter<string>();

    /**
     * Called when the user has scrolled to the end of the options list.
     */
    @Output()
    public onScrollEnd = new EventEmitter<number>();

    /**
     * Called when the list of options has been opened.
     */
    @Output()
    public optionsOpenedChange = new EventEmitter<boolean>();

    @ViewChild('aviDropdown')
    private elementRef: ElementRef;

    @ViewChild('aviDropdownOpener')
    private aviDropdownOpenerElementRef: ElementRef;

    /**
     * Reference to the div containing an optional transcluded [aviDropdown_selectedValuesContainer]
     * element.
     */
    @ViewChild('selectedValuesContainerContent')
    private selectedValuesContainer: ElementRef;

    @ViewChild(AviDropdownMenuComponent)
    private aviDropdownMenu: AviDropdownMenuComponent;

    public optionsPositionsPriority: ConnectedPosition[] = optionsPositionsPriority;
    public searchTerm = '';
    public required = false;
    public disabled = false;

    /**
     * True if the dropdown has a custom search binding passed in to filter options.
     */
    public customSearch = false;

    public viewMode = false;

    /**
     * Get keys from source bundles for template usage
     */
    public readonly l10nKeys = l10nKeys;

    public readonly dropdownOptionsControl$: Observable<boolean>;

    public readonly optionsLength$: Observable<void>;

    /**
     * Used to track changes in the number of options. This is used by AviTooltipDirective to update
     * the position if the position is no longer valid after the size of the options list has
     * changed.
     */
    public optionsLengthSubject = new Subject<void>();

    private modelValue: DropdownModelValue;

    private dropdownOptionsControlSubject = new Subject<boolean>();

    /**
     * Used to keep track of options that have been selected. Updated when this.value has been
     * changed and when the list of options has changed.
     */
    private selectedOptions: IAviDropdownOption[] = [];

    /**
     * Hash to keep track of hidden values for lookup.
     */
    private hiddenValuesHash: { [value: string]: true } = {};

    /**
     * Flag to indicate whether Angular NgControl is involed.
     * True when this component is used in pure Angular env.
     * False when this component is used in hybrid mode.
     */
    private hasNgControl = false;

    /**
     * Tracks if the dropdown options are currently open. Used to open/close options by keyboard
     * control.
     */
    private optionsOpened = false;

    private subscription = new Subscription();

    constructor(
        l10nService: L10nService,
        @Optional()
        fullModalComponentService: FullModalComponentService,
        // get access to the instance of FormControl
        @Optional()
        @Self()
        @Inject(NgControl)
        ngControl: NgControl,
        private readonly renderer: Renderer2,
    ) {
        if (ngControl) {
            // manually register this component as valueAccessor to make ControlValueAccessor work
            ngControl.valueAccessor = this;
            this.hasNgControl = true;
        }

        if (fullModalComponentService) {
            this.subscription.add(fullModalComponentService.viewMode$.subscribe(viewMode => {
                this.viewMode = viewMode;
            }));
        }

        l10nService.registerSourceBundles(dictionary);

        this.dropdownOptionsControl$ = this.dropdownOptionsControlSubject.asObservable();

        this.optionsLength$ = this.optionsLengthSubject.asObservable();

        this.placeholder = l10nService.getMessage(l10nKeys.selectPlaceholder);
    }

    /**
     * Handle keyboard actions.
     */
    @HostListener('keydown', ['$event.key'])
    private handleKeyPress(key: string): void {
        switch (key) {
            case 'Enter':
                this.dropdownOptionsControlSubject.next(!this.optionsOpened);
        }
    }

    /** @override */
    public ngOnInit(): void {
        if (this.hiddenValues.length) {
            this.setHiddenValuesHash();
        }

        this.customSearch = this.onSearch.observers.length > 0;
    }

    /** @override */
    public ngAfterViewInit(): void {
        // For accessibility, add aria-related attributes
        const identifier = this.id || this.name;
        const openerId = `${identifier}-opener`;
        const labelledBy = `${identifier}__avi-label-with-tooltip ${openerId}`;

        this.renderer.setAttribute(this.aviDropdownOpenerElementRef.nativeElement, 'id', openerId);
        this.renderer.setAttribute(this.elementRef.nativeElement, 'aria-labelledby', labelledBy);
    }

    /** @override */
    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        const { options, hiddenValues } = changes;

        if (options) {
            const { firstChange } = options;

            if (firstChange) {
                return;
            }

            this.setSelectedDropdownOptions();
        }

        if (hiddenValues) {
            this.setHiddenValuesHash();
        }
    }

    /**
     * Handle 'Escape' keydown from the dropdown menu.
     */
    public handleDropdownOptionsEscape(): void {
        if (this.hideSearch) {
            this.hideDropdownOptions();
        } else {
            this.aviDropdownMenu.focusSearch();
        }
    }

    /**
     * Returns true if the [aviDropdown_selectedValuesContainer] transcluded element exists.
     */
    public get hasTranscludedSelectedValuesContainer(): boolean {
        return this.selectedValuesContainer &&
            this.selectedValuesContainer.nativeElement.children.length > 0;
    }

    /**
     * Returns the height of the list of options.
     */
    public get optionsHeight(): number {
        const optionsLength = this.options.length - this.hiddenValues.length;

        return getOptionsHeight(optionsLength, OPTION_HEIGHT, MAX_OPTIONS_LIST_HEIGHT);
    }

    /**
     * Returns the width of the dropdown, used to set the max width of the dropdown options overlay.
     */
    public get elementWidth(): number {
        return this.elementRef ? this.elementRef.nativeElement.offsetWidth : 0;
    }

    /**
     * Returns a list of selected dropdown options.
     */
    public get selectedDropdownOptions(): IAviDropdownOption[] {
        return this.selectedOptions;
    }

    /**
     * Getter for the modelValue.
     */
    public get value(): DropdownModelValue {
        return this.modelValue;
    }

    /**
     * Setter for the modelValue. If multiple is set to true, then the modelValue takes the form of
     * an array. Otherwise it's just a string.
     */
    public set value(val: DropdownModelValue) {
        if (this.modelValue !== val) {
            const normalizedValue = normalizeValue(val);

            this.modelValue = normalizedValue;
            this.onChange(normalizedValue);
            this.setSelectedDropdownOptions();
        }

        this.onTouched();
    }

    /***************************************************************************
     * IMPLEMENTING ControlValueAccessor INTERFACE
    */

    /**
     * Sets the onChange function.
     */
    public registerOnChange(fn: (value: DropdownModelValue) => {}): void {
        this.onChange = fn;
    }

    /**
     * Writes the modelValue.
     */
    public writeValue(value: DropdownModelValue): void {
        this.modelValue = normalizeValue(value);
        this.setSelectedDropdownOptions();
    }

    /**
     * Sets the onTouched function.
     */
    public registerOnTouched(fn: () => {}): void {
        this.onTouched = fn;
    }

    /**
     * Sets the disabled state.
     */
    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    /*************************************************************************/

    /**
     * Called when a dropdown option has been selected. Adds the value to the modelValue array if
     * the dropdown is for multiple selection, otherwise replaces the current model value. We check
     * for instanceof Array so that TypeScript knows this.value is of type
     * DropdownModelSingleValue[] instead of type DropdownModelSingleValue.
     */
    public handleSelect(dropdownOption: IAviDropdownOption): void {
        if (containsDropdownOptionByValue(dropdownOption, this.selectedDropdownOptions)) {
            this.hideDropdownOptions();

            return;
        }

        const dropdownValue = getValue(dropdownOption);

        if (this.value instanceof Array) {
            this.value = [...this.value, dropdownValue];
        } else if (this.multiple) {
            this.value = [dropdownValue];
        } else {
            this.value = dropdownValue;
        }

        this.hideDropdownOptions();
    }

    /**
     * Click handler for clicking on a selected option. Applicable only to multiple selection. We
     * check for instanceof Array so that TypeScript knows this.value is of type
     * DropdownModelSingleValue[] instead of type DropdownModelSingleValue.
     */
    public handleRemoveValue(valueToRemove: DropdownModelValue): void {
        if (!(this.value instanceof Array)) {
            return;
        }

        this.value = this.value.filter(value => value !== valueToRemove);
    }

    /**
     * Called when user types into the search field. Emits the onSearch event if the search should
     * be handled by the parent.
     */
    public handleSearch(searchTerm: string): void {
        this.searchTerm = searchTerm;
        this.onSearch.emit(searchTerm);
    }

    /**
     * Called when the Clear All button has been clicked. Clears the model value.
     */
    public handleClearAll($event: MouseEvent): void {
        $event.stopPropagation();

        this.value = undefined;
    }

    /**
     * Returns true if the currently selected number of options is greater than or equal to the
     * maximum number of allowed options. We check for instanceof Array so that TypeScript knows
     * this.value is of type DropdownModelSingleValue[] instead of type DropdownModelSingleValue.
     */
    public reachedMaxSelected(): boolean {
        return this.multiple &&
            this.value instanceof Array &&
            this.value.length >= this.maxSelectableOptions;
    }

    /**
     * Event emitter for the dropdown options scroll end event. Typically used in
     * AviCollectionDropdown to load new options as the user gets to the end for an infinite scroll
     * effect.
     */
    public handleScrollEnd(total: number): void {
        this.onScrollEnd.emit(total);
    }

    /**
     * Event emitter for the dropdown options opening and closing. Typically used in
     * AviCollectionDropdown to load new options when the user opens the options.
     */
    public handleOpenedChange(opened: boolean): void {
        this.optionsOpened = opened;
        this.optionsOpenedChange.emit(opened);
    }

    /**
     * Returns a string of classes to apply to the container element.
     */
    public getContainerClassNames(): string {
        const containerClassName = 'avi-dropdown__container';

        return classnames(
            containerClassName,
            this.reachedMaxSelected() && `${containerClassName}--unclickable`,
            (this.readonly || this.viewMode) && `${containerClassName}--readonly`,
            this.disabled && `${containerClassName}--disabled`,
        );
    }

    public trackByValue(index: number, option: IAviDropdownOption): DropdownModelSingleValue {
        return option.value;
    }

    /**
     * Called when the dropdownOptions should be hidden.
     */
    public hideDropdownOptions(): void {
        this.searchTerm = '';
        this.dropdownOptionsControlSubject.next(false);
    }

    /**
     * Sets this.hiddenValuesHash to contain values that should be hidden from selection.
     */
    private setHiddenValuesHash(): void {
        this.hiddenValuesHash = this.hiddenValues.reduce((hash, value) => ({
            ...hash,
            [value]: true,
        }), {});
    }

    /**
     * Sets a list of selected dropdown options.
     */
    private setSelectedDropdownOptions(): void {
        if (isUndefined(this.value)) {
            this.selectedOptions = [];

            return;
        }

        const selectedValues = this.multiple ?
            this.value as DropdownModelSingleValue[] :
            [this.value];

        const selectedOptions = selectedValues
            .map(value => {
                const valueFromOptions = this.options.find(option => getValue(option) === value);

                return valueFromOptions ||
                    createDropdownOption(value as unknown as DropdownModelSingleValue);
            });

        // If selectedOptions have been found, set this.selectedOptions to those options. However,
        // it's possible that the value is populated while the options are not available (requires
        // an HTTP request for example), so we resort to showing the value itself in those cases.
        if (selectedOptions.length) {
            this.selectedOptions = selectedOptions;
        } else {
            this.selectedOptions = selectedValues.map((value: string) => {
                return createDropdownOption(value);
            });
        }
    }

    /**
     * Returns true if the dropdownOption should be hidden from selection.
     */
    private isHiddenValue = (dropdownOption: IAviDropdownOption): boolean => {
        const value = getValue(dropdownOption);

        return value in this.hiddenValuesHash;
    };

    /**
     * Method to be overridden by the ControlValueAccessor interface.
     */
    private onChange = (value: DropdownModelValue): void => {};

    /**
     * Method to be overridden by the ControlValueAccessor interface.
     */
    private onTouched = (): void => {};
}
